// =========================================================
// DEBUTS CROP
// PREMIUM GROW SHOP
// =========================================================
//
// BlogArticle.jsx
//
// ARTÍCULO DINÁMICO DESDE DJANGO API
//
// DESARROLLO:
// VITE_API_URL=http://localhost:8000/api
//
// PRODUCCIÓN:
// VITE_API_URL=https://api.debutscrop.com/api
//
// =========================================================

import {
  useEffect,
  useState,
} from "react";

import {
  Link,
  useParams,
} from "react-router-dom";

import "./blog.css";

import blogBg from "../../assets/bg.png";

import Navbar from "../../components/common/Navbar";
import Footer from "../../components/common/Footer";


// =========================================================
// API
// =========================================================
//
// IMPORTANTE:
//
// No colocar aquí la URL de producción manualmente.
//
// Vite utiliza:
//
// .env.development
// .env.production
//
// =========================================================

const API_URL = (
  import.meta.env.VITE_API_URL ||
  "http://localhost:8000/api"
).replace(/\/+$/, "");


// =========================================================
// API URL
// =========================================================

function getApiUrl(path = "") {

  const cleanPath =
    String(path).startsWith("/")
      ? path
      : `/${path}`;

  return `${API_URL}${cleanPath}`;
}


// =========================================================
// BACKEND ORIGIN
// =========================================================
//
// http://localhost:8000/api
//        ↓
// http://localhost:8000
//
// https://api.debutscrop.com/api
//        ↓
// https://api.debutscrop.com
//
// =========================================================

function getBackendOrigin() {

  return API_URL
    .replace(/\/api\/?$/, "")
    .replace(/\/+$/, "");
}


// =========================================================
// IMAGE URL
// =========================================================

function getImageUrl(image) {

  if (!image) {
    return blogBg;
  }

  if (
    typeof image !== "string"
  ) {
    return blogBg;
  }

  const value =
    image.trim();

  if (!value) {
    return blogBg;
  }


  // URL absoluta

  if (
    value.startsWith("http://") ||
    value.startsWith("https://") ||
    value.startsWith("data:") ||
    value.startsWith("blob:")
  ) {
    return value;
  }


  // URL relativa de Django

  if (
    value.startsWith("/")
  ) {
    return `${getBackendOrigin()}${value}`;
  }


  // Ruta relativa

  return `${getBackendOrigin()}/${value.replace(/^\/+/, "")}`;
}


// =========================================================
// API RESULTS
// =========================================================
//
// Soporta:
//
// [
//   {...}
// ]
//
// y:
//
// {
//   count: 10,
//   results: [...]
// }
//
// =========================================================

function getResults(data) {

  if (
    Array.isArray(data)
  ) {
    return data;
  }

  if (
    Array.isArray(data?.results)
  ) {
    return data.results;
  }

  return [];
}


// =========================================================
// DATE
// =========================================================

function formatDate(date) {

  if (!date) {
    return "";
  }

  const parsedDate =
    new Date(date);

  if (
    Number.isNaN(
      parsedDate.getTime()
    )
  ) {
    return String(date);
  }

  return parsedDate.toLocaleDateString(
    "es-CO",
    {
      day: "2-digit",
      month: "long",
      year: "numeric",
    }
  );
}


// =========================================================
// CATEGORY NAME
// =========================================================

function getCategoryName(article) {

  if (!article) {
    return "BLOG";
  }

  if (
    article.category_name
  ) {
    return article.category_name;
  }

  if (
    article.category &&
    typeof article.category === "object"
  ) {
    return (
      article.category.name ||
      "BLOG"
    );
  }

  if (
    typeof article.category === "string"
  ) {
    return article.category;
  }

  return "BLOG";
}


// =========================================================
// CATEGORY SLUG
// =========================================================

function getCategorySlug(article) {

  if (!article) {
    return "";
  }

  if (
    article.category_slug
  ) {
    return article.category_slug;
  }

  if (
    article.category &&
    typeof article.category === "object"
  ) {
    return (
      article.category.slug ||
      ""
    );
  }

  return "";
}


// =========================================================
// AUTHOR
// =========================================================

function getAuthorName(article) {

  if (!article) {
    return "Debuts Crop";
  }

  if (
    article.author_name
  ) {
    return article.author_name;
  }

  if (
    article.author &&
    typeof article.author === "object"
  ) {
    return (
      article.author.name ||
      article.author.username ||
      "Debuts Crop"
    );
  }

  if (
    typeof article.author === "string"
  ) {
    return article.author;
  }

  return "Debuts Crop";
}


// =========================================================
// READING TIME
// =========================================================

function getReadingTime(article) {

  if (!article) {
    return "";
  }

  if (
    article.reading_time
  ) {
    return `${article.reading_time} min`;
  }

  if (
    article.read_time
  ) {
    return article.read_time;
  }

  if (
    article.readTime
  ) {
    return article.readTime;
  }

  return "";
}


// =========================================================
// NORMALIZE ARTICLE
// =========================================================

function normalizeArticle(article) {

  if (!article) {
    return null;
  }

  return {
    ...article,

    image:
      getImageUrl(
        article.cover_image ||
        article.image ||
        ""
      ),

    categoryName:
      getCategoryName(article),

    categorySlug:
      getCategorySlug(article),

    date:
      formatDate(
        article.published_at ||
        article.created_at
      ),

    authorName:
      getAuthorName(article),

    readTime:
      getReadingTime(article),

    excerpt:
      article.excerpt ||
      "",

    contentHeading:
      article.content_heading ||
      article.title ||
      "",

    blocks:
      Array.isArray(
        article.blocks
      )
        ? article.blocks
        : [],
  };
}


// =========================================================
// CHECK INTERNAL URL
// =========================================================

function isInternalUrl(url) {

  if (!url) {
    return true;
  }

  return (
    url.startsWith("/") ||
    url.startsWith("#")
  );
}


// =========================================================
// ARTICLE LINK
// =========================================================

function ArticleLink({
  url,
  children,
  className = "",
}) {

  const targetUrl =
    url || "/catalog";


  // INTERNAL

  if (
    isInternalUrl(
      targetUrl
    )
  ) {

    return (
      <Link
        to={targetUrl}
        className={className}
      >
        {children}
      </Link>
    );

  }


  // EXTERNAL

  return (
    <a
      href={targetUrl}
      className={className}
      target="_blank"
      rel="noopener noreferrer"
    >
      {children}
    </a>
  );
}


// =========================================================
// ARTICLE BLOCKS
// =========================================================

function ArticleBlocks({
  blocks,
}) {

  if (
    !Array.isArray(blocks) ||
    blocks.length === 0
  ) {
    return null;
  }


  return (
    <div className="article-blocks">

      {blocks.map(
        (block, index) => {

          if (!block) {
            return null;
          }


          const key =
            block.id ||
            `block-${index}`;


          const image =
            getImageUrl(
              block.image
            );


          // =================================================
          // HEADING
          // =================================================

          if (
            block.block_type ===
            "heading"
          ) {

            return (
              <section
                key={key}
                className="article-product"
              >

                {block.title && (
                  <h3>
                    {block.title}
                  </h3>
                )}

                {block.subtitle && (
                  <p>
                    {block.subtitle}
                  </p>
                )}

                {block.text && (
                  <p>
                    {block.text}
                  </p>
                )}

              </section>
            );
          }


          // =================================================
          // PARAGRAPH
          // =================================================

          if (
            block.block_type ===
            "paragraph"
          ) {

            return (
              <p
                key={key}
              >
                {block.text}
              </p>
            );
          }


          // =================================================
          // IMAGE
          // =================================================

          if (
            block.block_type ===
            "image"
          ) {

            return (
              <div
                key={key}
                className="article-product-image"
              >

                <img
                  src={
                    image
                  }
                  alt={
                    block.image_alt ||
                    block.title ||
                    "Imagen del artículo"
                  }
                  loading="lazy"
                />

              </div>
            );
          }


          // =================================================
          // PRODUCT
          // =================================================

          if (
            block.block_type ===
            "product"
          ) {

            return (
              <section
                key={key}
                className="article-product"
              >

                {block.title && (
                  <h3>
                    {block.title}
                  </h3>
                )}


                <div className="article-product-row">

                  <div className="article-product-image">

                    <img
                      src={
                        image
                      }
                      alt={
                        block.image_alt ||
                        block.title ||
                        "Producto Debuts Crop"
                      }
                      loading="lazy"
                    />

                  </div>


                  <div className="article-product-content">

                    {block.text && (
                      <p>
                        {block.text}
                      </p>
                    )}


                    {Array.isArray(
                      block.bullets
                    ) &&
                      block.bullets.length >
                        0 && (

                        <ul>

                          {block.bullets.map(
                            (
                              bullet,
                              bulletIndex
                            ) => (

                              <li
                                key={
                                  bulletIndex
                                }
                              >
                                ● {bullet}
                              </li>

                            )
                          )}

                        </ul>

                      )}


                    {block.product_id && (

                      <Link
                        to={`/catalog?product=${block.product_id}`}
                        className="article-button"
                      >
                        VER EN CATÁLOGO →
                      </Link>

                    )}

                  </div>

                </div>

              </section>
            );
          }


          // =================================================
          // LIST
          // =================================================

          if (
            block.block_type ===
            "list"
          ) {

            return (
              <div
                key={key}
                className="article-product-content"
              >

                {block.title && (
                  <h3>
                    {block.title}
                  </h3>
                )}


                {Array.isArray(
                  block.bullets
                ) &&
                  block.bullets.length >
                    0 && (

                    <ul>

                      {block.bullets.map(
                        (
                          bullet,
                          bulletIndex
                        ) => (

                          <li
                            key={
                              bulletIndex
                            }
                          >
                            ● {bullet}
                          </li>

                        )
                      )}

                    </ul>

                  )}

              </div>
            );
          }


          // =================================================
          // QUOTE
          // =================================================

          if (
            block.block_type ===
            "quote"
          ) {

            return (
              <blockquote
                key={key}
              >
                {block.text}
              </blockquote>
            );
          }


          // =================================================
          // BUTTON
          // =================================================

          if (
            block.block_type ===
            "button"
          ) {

            return (
              <div
                key={key}
                className="article-navigation"
              >

                <ArticleLink
                  url={
                    block.button_url ||
                    "/catalog"
                  }
                  className="article-button"
                >
                  {block.button_text ||
                    "VER MÁS"} →
                </ArticleLink>

              </div>
            );
          }


          // =================================================
          // DIVIDER
          // =================================================

          if (
            block.block_type ===
            "divider"
          ) {

            return (
              <hr
                key={key}
              />
            );
          }


          return null;
        }
      )}

    </div>
  );
}


// =========================================================
// SIDEBAR
// =========================================================

function BlogSidebar({
  categories,
  popular,
  pageSettings,
}) {

  const [
    search,
    setSearch,
  ] = useState("");


  // =======================================================
  // SEARCH
  // =======================================================

  function handleSearch(
    event
  ) {

    event.preventDefault();

    const value =
      search.trim();

    if (!value) {

      window.location.href =
        "/blog";

      return;
    }

    window.location.href =
      `/blog?search=${encodeURIComponent(
        value
      )}`;
  }


  return (
    <aside className="blog-sidebar">


      {/* =================================================
          SEARCH
      ================================================= */}

      <form
        className="sidebar-search"
        onSubmit={
          handleSearch
        }
      >

        <span>
          ⌕
        </span>

        <input
          type="search"
          value={search}
          onChange={(
            event
          ) =>
            setSearch(
              event.target.value
            )
          }
          placeholder={
            pageSettings?.search_placeholder ||
            "Buscar en el blog..."
          }
          aria-label="Buscar en el blog"
        />

      </form>


      {/* =================================================
          CATEGORIES
      ================================================= */}

      <section className="sidebar-box">

        <h3>

          ♛ &nbsp;

          {
            pageSettings?.categories_title ||
            "CATEGORÍAS"
          }

        </h3>


        {categories.length >
        0 ? (

          categories.map(
            (category) => (

              <Link
                key={
                  category.id ||
                  category.slug
                }
                to={`/blog?category=${encodeURIComponent(
                  category.slug
                )}`}
              >

                <span>
                  {
                    category.name
                  }
                </span>

                <b>
                  {
                    Number(
                      category.article_count ||
                      0
                    )
                  }
                </b>

              </Link>

            )
          )

        ) : (

          <span>
            No hay categorías disponibles.
          </span>

        )}

      </section>


      {/* =================================================
          POPULAR
      ================================================= */}

      <section className="sidebar-box">

        <h3>

          ♨ &nbsp;

          {
            pageSettings?.popular_title ||
            "ARTÍCULOS POPULARES"
          }

        </h3>


        {popular.length >
        0 ? (

          popular.map(
            (item) => (

              <Link
                className="popular-post"
                to={`/blog/${item.slug}`}
                key={
                  item.slug ||
                  item.id
                }
              >

                <img
                  src={
                    item.image ||
                    blogBg
                  }
                  alt={
                    item.title ||
                    "Artículo"
                  }
                  loading="lazy"
                  onError={(
                    event
                  ) => {

                    if (
                      event.currentTarget.src !==
                      blogBg
                    ) {

                      event.currentTarget.src =
                        blogBg;

                    }

                  }}
                />

                <span>

                  <strong>
                    {
                      item.title
                    }
                  </strong>

                  <small>
                    {
                      item.date
                    }
                  </small>

                </span>

              </Link>

            )
          )

        ) : (

          <span>
            No hay artículos populares.
          </span>

        )}

      </section>


      {/* =================================================
          SUBSCRIBE
      ================================================= */}

      <section className="subscribe-box">

        <h3>

          ✉ &nbsp;

          {
            pageSettings?.subscribe_title ||
            "SUSCRÍBETE"
          }

        </h3>

        <p>

          {
            pageSettings?.subscribe_text ||
            "Recibe nuestras novedades, promociones y artículos exclusivos directamente en tu correo."
          }

        </p>

        <input
          type="email"
          placeholder={
            pageSettings?.subscribe_placeholder ||
            "Tu correo electrónico"
          }
        />

        <button
          type="button"
        >
          {
            pageSettings?.subscribe_button_text ||
            "SUBSCRIBIRME"
          }
        </button>

      </section>


      {/* =================================================
          PROMO
      ================================================= */}

      <div
        className="sidebar-promo"
        style={{
          backgroundImage:
            `url(${
              getImageUrl(
                pageSettings?.promo_image
              )
            })`,
        }}
      >

        <strong>
          ♛
        </strong>

        <span>

          {
            pageSettings?.promo_text ? (

              pageSettings.promo_text

            ) : (

              <>
                EL CONOCIMIENTO
                <br />
                TAMBIÉN ES CULTIVO
              </>

            )
          }

        </span>

      </div>

    </aside>
  );
}


// =========================================================
// ARTICLE EMPTY CONTENT
// =========================================================
//
// Este componente se muestra cuando:
//
// - El artículo existe
// - Está publicado
// - Pero todavía no tiene contenido
// - No tiene bloques
//
// =========================================================

function EmptyArticleContent() {

  return (
    <div className="blog-empty article-empty">

      <span className="state-label">
        DEBUTS CROP · ARTICLE / 00
      </span>

      <h3>

        ESTE ARTÍCULO

        <br />

        <em>
          ESTÁ CRECIENDO.
        </em>

      </h3>

      <p>

        Este artículo ya forma parte de
        nuestro blog, pero todavía estamos
        preparando su contenido.

        <br />

        Muy pronto encontrarás aquí
        información, consejos y contenido
        exclusivo de Debuts Crop.

      </p>

      <Link
        to="/blog"
        className="blog-empty-button"
      >
        EXPLORAR TODO EL BLOG →

      </Link>

    </div>
  );
}


// =========================================================
// LOADING PAGE
// =========================================================

function ArticleLoading() {

  return (
    <div
      className="blog-page article-page"
      style={{
        "--blog-bg":
          `url(${blogBg})`,
      }}
    >

      <Navbar />

      <main>

        <div className="blog-layout">

          <article className="article-main">

            <section
              className="article-hero"
              style={{
                backgroundImage:
                  `url(${blogBg})`,
              }}
            >

              <div className="article-hero-overlay">

                <span className="article-category">
                  BLOG
                </span>

                <h1>
                  CARGANDO...
                </h1>

                <p>
                  Estamos preparando
                  el contenido.
                </p>

              </div>

            </section>

          </article>

        </div>

      </main>

      <Footer />

    </div>
  );
}


// =========================================================
// ARTICLE ERROR
// =========================================================

function ArticleError({
  message,
}) {

  return (
    <div
      className="blog-page article-page"
      style={{
        "--blog-bg":
          `url(${blogBg})`,
      }}
    >

      <Navbar />

      <main>

        <div className="blog-layout">

          <article className="article-main">

            <section
              className="article-hero"
              style={{
                backgroundImage:
                  `url(${blogBg})`,
              }}
            >

              <div className="article-hero-overlay">

                <span className="article-category">
                  BLOG / 404
                </span>

                <h1>

                  ARTÍCULO

                  <br />

                  <em>
                    NO ENCONTRADO
                  </em>

                </h1>

                <p>
                  {
                    message ||
                    "El artículo que buscas no existe o ya no está disponible."
                  }
                </p>

                <Link
                  to="/blog"
                  className="article-hero-button"
                >
                  VOLVER AL BLOG →
                </Link>

              </div>

            </section>

          </article>

        </div>

      </main>

      <Footer />

    </div>
  );
}


// =========================================================
// BLOG ARTICLE
// =========================================================

export default function BlogArticle() {

  const {
    slug,
  } = useParams();


  // =======================================================
  // STATE
  // =======================================================

  const [
    post,
    setPost,
  ] = useState(null);

  const [
    categories,
    setCategories,
  ] = useState([]);

  const [
    popular,
    setPopular,
  ] = useState([]);

  const [
    pageSettings,
    setPageSettings,
  ] = useState(null);

  const [
    related,
    setRelated,
  ] = useState([]);

  const [
    loading,
    setLoading,
  ] = useState(true);

  const [
    error,
    setError,
  ] = useState("");


  // =======================================================
  // LOAD ARTICLE
  // =======================================================

  useEffect(() => {

    const controller =
      new AbortController();


    async function loadArticle() {

      if (!slug) {

        setError(
          "No se encontró el artículo."
        );

        setLoading(false);

        return;
      }


      setLoading(true);
      setError("");


      try {

        const response =
          await fetch(
            getApiUrl(
              `/blog/articles/${encodeURIComponent(
                slug
              )}/`
            ),
            {
              method: "GET",
              headers: {
                Accept:
                  "application/json",
              },
              signal:
                controller.signal,
            }
          );


        if (
          response.status ===
          404
        ) {

          throw new Error(
            "El artículo no existe o no está publicado."
          );

        }


        if (
          !response.ok
        ) {

          throw new Error(
            `Error HTTP ${response.status}`
          );

        }


        const data =
          await response.json();


        setPost(
          normalizeArticle(
            data
          )
        );


      } catch (err) {

        if (
          err.name ===
          "AbortError"
        ) {
          return;
        }


        console.error(
          "BLOG ARTICLE:",
          err
        );


        setPost(null);

        setError(
          err.message ||
          "No fue posible cargar el artículo."
        );


      } finally {

        if (
          !controller.signal.aborted
        ) {

          setLoading(false);

        }

      }

    }


    loadArticle();


    return () => {
      controller.abort();
    };

  }, [
    slug,
  ]);


  // =======================================================
  // LOAD CATEGORIES
  // =======================================================

  useEffect(() => {

    const controller =
      new AbortController();


    async function loadCategories() {

      try {

        const response =
          await fetch(
            getApiUrl(
              "/blog/categories/?is_active=true"
            ),
            {
              headers: {
                Accept:
                  "application/json",
              },
              signal:
                controller.signal,
            }
          );


        if (
          !response.ok
        ) {
          return;
        }


        const data =
          await response.json();


        setCategories(
          getResults(data)
        );


      } catch (err) {

        if (
          err.name !==
          "AbortError"
        ) {

          console.error(
            "BLOG CATEGORIES:",
            err
          );

        }

      }

    }


    loadCategories();


    return () => {
      controller.abort();
    };

  }, []);


  // =======================================================
  // LOAD POPULAR
  // =======================================================

  useEffect(() => {

    const controller =
      new AbortController();


    async function loadPopular() {

      try {

        const response =
          await fetch(
            getApiUrl(
              "/blog/articles/popular/?limit=5"
            ),
            {
              headers: {
                Accept:
                  "application/json",
              },
              signal:
                controller.signal,
            }
          );


        if (
          !response.ok
        ) {
          return;
        }


        const data =
          await response.json();


        setPopular(
          getResults(data)
            .map(
              normalizeArticle
            )
            .filter(Boolean)
        );


      } catch (err) {

        if (
          err.name !==
          "AbortError"
        ) {

          console.error(
            "BLOG POPULAR:",
            err
          );

        }

      }

    }


    loadPopular();


    return () => {
      controller.abort();
    };

  }, []);


  // =======================================================
  // LOAD PAGE SETTINGS
  // =======================================================

  useEffect(() => {

    const controller =
      new AbortController();


    async function loadSettings() {

      try {

        const response =
          await fetch(
            getApiUrl(
              "/blog/settings/"
            ),
            {
              headers: {
                Accept:
                  "application/json",
              },
              signal:
                controller.signal,
            }
          );


        if (
          !response.ok
        ) {
          return;
        }


        const data =
          await response.json();


        setPageSettings(
          data
        );


      } catch (err) {

        if (
          err.name !==
          "AbortError"
        ) {

          console.error(
            "BLOG SETTINGS:",
            err
          );

        }

      }

    }


    loadSettings();


    return () => {
      controller.abort();
    };

  }, []);


  // =======================================================
  // LOAD RELATED
  // =======================================================

  useEffect(() => {

    if (!slug) {
      return;
    }


    const controller =
      new AbortController();


    async function loadRelated() {

      try {

        const response =
          await fetch(
            getApiUrl(
              `/blog/articles/${encodeURIComponent(
                slug
              )}/related/?limit=2`
            ),
            {
              headers: {
                Accept:
                  "application/json",
              },
              signal:
                controller.signal,
            }
          );


        if (
          !response.ok
        ) {
          return;
        }


        const data =
          await response.json();


        setRelated(
          getResults(data)
            .map(
              normalizeArticle
            )
            .filter(Boolean)
        );


      } catch (err) {

        if (
          err.name !==
          "AbortError"
        ) {

          console.error(
            "BLOG RELATED:",
            err
          );

        }

      }

    }


    loadRelated();


    return () => {
      controller.abort();
    };

  }, [
    slug,
  ]);


  // =======================================================
  // LOADING
  // =======================================================

  if (loading) {

    return (
      <ArticleLoading />
    );

  }


  // =======================================================
  // ERROR
  // =======================================================

  if (
    error ||
    !post
  ) {

    return (
      <ArticleError
        message={
          error
        }
      />
    );

  }


  // =======================================================
  // ARTICLE DATA
  // =======================================================

  const category =
    post.categoryName ||
    "BLOG";


  const categorySlug =
    post.categorySlug ||
    "";


  const heroButtonEnabled =
    post.hero_button_enabled === true;


  const heroButtonText =
    post.hero_button_text ||
    "VER EN CATÁLOGO";


  const defaultHeroUrl =
    categorySlug
      ? `/catalog?category=${encodeURIComponent(
          categorySlug
        )}`
      : "/catalog";


  const heroButtonUrl =
    post.hero_button_url ||
    defaultHeroUrl;


  const contentHeading =
    post.contentHeading ||
    post.title ||
    "";


  // =======================================================
  // CONTENT
  // =======================================================

  const rawContent =
    typeof post.content ===
    "string"
      ? post.content.trim()
      : "";


  // =======================================================
  // HAS BLOCKS
  // =======================================================

  const hasBlocks =
    Array.isArray(
      post.blocks
    ) &&
    post.blocks.length >
      0;


  // =======================================================
  // HAS CONTENT
  // =======================================================

  const hasContent =
    Boolean(
      rawContent
    ) ||
    hasBlocks;


  // =======================================================
  // CONTENT PARAGRAPHS
  // =======================================================
  //
  // Si `content` contiene texto normal,
  // lo dividimos por saltos de línea.
  //
  // No usamos dangerouslySetInnerHTML.
  //
  // =======================================================

  const contentParagraphs =
    rawContent
      ? rawContent
          .split(/\n\s*\n/)
          .map(
            (paragraph) =>
              paragraph.trim()
          )
          .filter(Boolean)
      : [];


  // =======================================================
  // RENDER
  // =======================================================

  return (
    <div
      className="blog-page article-page"
      style={{
        "--blog-bg":
          `url(${blogBg})`,
      }}
    >


      {/* =================================================
          NAVBAR
      ================================================= */}

      <Navbar />


      {/* =================================================
          MAIN
      ================================================= */}

      <main>


        {/* =================================================
            BREADCRUMB
        ================================================= */}

        <div className="article-breadcrumb">

          <Link to="/">
            Inicio
          </Link>

          <span>
            ›
          </span>

          <Link to="/blog">
            Blog
          </Link>

          <span>
            ›
          </span>

          <Link
            to={
              categorySlug
                ? `/blog?category=${encodeURIComponent(
                    categorySlug
                  )}`
                : "/blog"
            }
          >
            {category}
          </Link>

          <span>
            ›
          </span>

          <span>
            {post.title}
          </span>

        </div>


        {/* =================================================
            LAYOUT
        ================================================= */}

        <div className="blog-layout">


          {/* =================================================
              ARTICLE
          ================================================= */}

          <article className="article-main">


            {/* ===============================================
                HERO
            =============================================== */}

            <section
              className="article-hero"
              style={{
                backgroundImage:
                  `url(${
                    post.image ||
                    blogBg
                  })`,
              }}
            >

              <div className="article-hero-overlay">


                {/* CATEGORY */}

                <span className="article-category">

                  {category}

                </span>


                {/* TITLE */}

                <h1>

                  {post.title}

                </h1>


                {/* EXCERPT */}

                {post.excerpt && (

                  <p>

                    {post.excerpt}

                  </p>

                )}


                {/* HERO BUTTON */}

                {heroButtonEnabled && (

                  <ArticleLink
                    url={
                      heroButtonUrl
                    }
                    className="article-hero-button"
                  >
                    {heroButtonText}
                    {" "}
                    →
                  </ArticleLink>

                )}

              </div>

            </section>


            {/* ===============================================
                META
            =============================================== */}

            <div className="article-meta">


              {post.show_date !==
                false && (
                <span>

                  ▣ &nbsp;

                  {
                    post.date
                  }

                </span>
              )}


              {post.show_author !==
                false && (
                <span>

                  ♙ &nbsp;

                  Por{" "}

                  {
                    post.authorName
                  }

                </span>
              )}


              {post.show_category !==
                false && (
                <span>

                  ♧ &nbsp;

                  {
                    category
                  }

                </span>
              )}


              {post.show_reading_time !==
                false &&
                post.readTime && (

                <span>

                  ◷ &nbsp;

                  {
                    post.readTime
                  }

                </span>

              )}

            </div>


            {/* ===============================================
                BODY
            =============================================== */}

            <section className="article-body">


              {/* =============================================
                  HEADING
              ============================================= */}

              {contentHeading && (

                <h2>
                  {contentHeading}
                </h2>

              )}


              {/* =============================================
                  CONTENT TEXT
              ============================================= */}

              {contentParagraphs.length >
                0 && (

                <div className="article-content-text">

                  {contentParagraphs.map(
                    (
                      paragraph,
                      index
                    ) => (

                      <p
                        key={
                          `paragraph-${index}`
                        }
                      >
                        {paragraph}
                      </p>

                    )
                  )}

                </div>

              )}


              {/* =============================================
                  DYNAMIC BLOCKS
              ============================================= */}

              {hasBlocks && (

                <ArticleBlocks
                  blocks={
                    post.blocks
                  }
                />

              )}


              {/* =============================================
                  EMPTY ARTICLE
              ============================================= */}

              {!hasContent && (

                <EmptyArticleContent />

              )}


              {/* =============================================
                  RELATED ARTICLES
              ============================================= */}

              {related.length >
                0 && (

                <section className="article-related">

                  <div className="section-heading">

                    <h2>
                      ♛ ARTÍCULOS RELACIONADOS
                    </h2>

                  </div>


                  <div className="post-grid">

                    {related.map(
                      (item) => (

                        <article
                          className="post-card"
                          key={
                            item.slug ||
                            item.id
                          }
                        >

                          <Link
                            to={`/blog/${item.slug}`}
                            className="post-image"
                          >

                            <img
                              src={
                                item.image ||
                                blogBg
                              }
                              alt={
                                item.title ||
                                "Artículo relacionado"
                              }
                              loading="lazy"
                              onError={(
                                event
                              ) => {

                                if (
                                  event.currentTarget.src !==
                                  blogBg
                                ) {

                                  event.currentTarget.src =
                                    blogBg;

                                }

                              }}
                            />

                            <span>

                              {
                                item.categoryName
                              }

                            </span>

                          </Link>


                          <div className="post-body">

                            <small>

                              {
                                item.date
                              }

                            </small>

                            <h3>

                              {
                                item.title
                              }

                            </h3>

                            <p>

                              {
                                item.excerpt
                              }

                            </p>

                            <Link
                              to={`/blog/${item.slug}`}
                              className="read-more"
                            >
                              Leer más →
                            </Link>

                          </div>

                        </article>

                      )
                    )}

                  </div>

                </section>

              )}


              {/* =============================================
                  ARTICLE NAVIGATION
              ============================================= */}

              <div className="article-navigation">

                <Link to="/blog">

                  ← VOLVER AL BLOG

                </Link>


                <Link to="/blog">

                  ▦ TODOS LOS ARTÍCULOS

                </Link>


                <Link to="/catalog">

                  EXPLORAR CATÁLOGO →

                </Link>

              </div>


            </section>

          </article>


          {/* =================================================
              SIDEBAR
          ================================================= */}

          <BlogSidebar
            categories={
              categories
            }
            popular={
              popular
            }
            pageSettings={
              pageSettings
            }
          />

        </div>

      </main>


      {/* =================================================
          FOOTER
      ================================================= */}

      <Footer />

    </div>
  );
}